/* eslint-disable complexity */
import Store from 'store';
import {emitGE} from './edap';
import {normalize} from './string';

// Flag for ensuring only 1 exposure event is raised
let observedRecents = false;
let getRecentSearchesSortedHashMap = {}; // Perf Hash Map

export const RECENT_SEARCHES_LOCAL_STORAGE_KEY =
    'react-destination-typeahead-recent-searches';
export const RECENT_SEARCHES_SESSION_EXPOSURE_KEY =
    'react-destination-typeahead-session-exposure';
export const MAX_LOCALSTORAGE_ITEMS = 100;
export const MAX_LOCALSTORAGE_EXPIRY =
    parseInt(Date.now(), 10) - 86400 * 28 * 1000; // 28 days

/**
 * resetHashMap()
 * Clears perf optimized hash map
 */
export function resetHashMap() {
    getRecentSearchesSortedHashMap = {};
}
/**
 * filterObject
 * Helper function to filter JS objects
 * @param {*} obj
 * @param {*} predicate
 */
export function filterObject(obj, predicate) {
    let result = {};
    let key = null;
    for (key in obj) {
        if (obj.hasOwnProperty(key) && !predicate(key, obj[key])) {
            result[key] = obj[key];
        }
    }
    return result;
}

/**
 * getRecentSearches()
 * Get recent searches from local storage
 */
export function getRecentSearches(recentSearchesLimit) {
    let recentSearches =
        Store.enabled && Store.get(RECENT_SEARCHES_LOCAL_STORAGE_KEY);
    if (recentSearches && recentSearches.length > 0) {
        if (recentSearchesLimit) {
            return recentSearches.slice(0, recentSearchesLimit);
        }
        return recentSearches;
    }
    return [];
}

/**
 * setRecentSearches()
 * Update recent search local storage
 * @param {*} data
 */
export function setRecentSearches(data) {
    return Store.enabled && Store.set(RECENT_SEARCHES_LOCAL_STORAGE_KEY, data);
}

/**
 * pruneLocalStorage()
 * Cleans up localstorage items related to typeahead
 */
export function pruneLocalStorage() {
    let items = getRecentSearches();
    let itemsPruned = 0;
    let i = 0;
    let item = null;

    // remove expired entries first.
    // iterate in reverse so we can safely remove items
    for (i = items.length - 1; i > -1; i--) {
        item = items[i];
        if (item.expireTime < MAX_LOCALSTORAGE_EXPIRY) {
            items.splice(i, 1);
            itemsPruned++;
        }
    }

    // if still exceeds max, sort and cut off oldest to maximum
    if (items.length > MAX_LOCALSTORAGE_ITEMS) {
        // perform sort
        items = items.sort((a, b) => {
            if (a.expireTime > b.expireTime) {
                return -1;
            }
            if (a.expireTime < b.expireTime) {
                return 1;
            }
            return 0;
        });

        for (i = items.length - 1; i > -1 + MAX_LOCALSTORAGE_ITEMS; i--) {
            items.splice(i, 1);
            itemsPruned++;
        }
    }

    if (itemsPruned > 0) {
        setRecentSearches(items);
    }

    return itemsPruned;
}

/**
 * newRecentSearchMatch()
 * Function to augment search matches with a "score"
 * @param {*} query - the current user search query that is entered into the search box
 * @param {*} lang - the language so transliterate can properly tokenize
 * @param {*} recentSearch - the search result
 */
export function newRecentSearchMatch(query, lang, recentSearch) {
    let normalizedQ = normalize(query, lang);
    let normalizedTerm = normalize(recentSearch.selectedDestination.term);
    let normalizedQTokens = normalizedQ.split(' ');

    let score = null;

    // Look for strict prefix matches first
    if (normalizedTerm.indexOf(normalizedQ) === 0) {
        score = 0;
    } else {
        score = 1;
        for (let i = 0; i < normalizedQTokens.length && score !== null; i++) {
            let normalizedQToken = normalizedQTokens[i];
            let regex = new RegExp(`\\b${normalizedQToken}`);
            if (!regex.test(normalizedTerm)) {
                score = null;
            }
        }
    }

    return {
        recentSearch,
        score
    };
}

/**
 * addRecentSearch()
 * @param {obj} state - Add a recent search to local storage}
 */
export function addRecentSearch(selectedDestination) {
    let selectedDestinationFiltered = filterObject(
        selectedDestination,
        (key) => {
            return key === 'highlightedTerm';
        }
    );

    // Create persistence object intended for user local storage
    let data = {
        selectedDestination: selectedDestinationFiltered
    };

    // Do a search for currently stored recents
    let currentData = getRecentSearches();

    // Filter out old instance of recent term and limit storage to 100 items
    currentData = currentData
        .map((recentSearch) => {
            // Filter bad data or uuid match
            if (
                !recentSearch ||
                !recentSearch.selectedDestination ||
                recentSearch.selectedDestination.uuid ===
                    selectedDestinationFiltered.uuid
            ) {
                return false;
            }
            return recentSearch;
        })
        .filter((i) => {
            return i;
        })
        .slice(0, MAX_LOCALSTORAGE_ITEMS);

    // Add expireTime property
    data.expireTime = new Date().getTime() + MAX_LOCALSTORAGE_EXPIRY; // add 4 week TTL

    // Add new search to collection
    currentData.unshift(data);

    // Update local storage
    setRecentSearches(currentData);
}

/**
 * getRecentSearchesSorted()
 * Get recent searches + client side computation
 */
export function getRecentSearchesSorted(locale, search, max) {
    if (getRecentSearchesSortedHashMap[search]) {
        return getRecentSearchesSortedHashMap[search];
    }
    // If locale is something other than 'en', pass it into getRecentSearches for client-side lookup optimization
    let lang = locale !== 'en' ? locale : null;
    let query = search || '';
    let recentSearchesLimit = max || 3;
    let recentSearches = getRecentSearches(); // TODO: optimize access to this / cache
    let bestRecentSearches = [];
    let recentSearchMatches = [];

    for (let i = 0; i < recentSearches.length; i++) {
        let recentSearch = recentSearches[i];
        // if valid data model
        if (
            recentSearch.selectedDestination &&
            recentSearch.selectedDestination.term &&
            recentSearch.selectedDestination.uuid
        ) {
            let recentSearchMatch = newRecentSearchMatch(
                query,
                lang,
                recentSearch
            );
            if (recentSearchMatch.score !== null) {
                recentSearchMatches.push(recentSearchMatch);
            }
        }
    }

    recentSearchMatches.sort((a, b) => {
        return a.score - b.score;
    });

    // Pick the first set of searches (recentSearchesLimit)
    for (
        let i = 0;
        i < Math.min(recentSearchesLimit, recentSearchMatches.length);
        i++
    ) {
        bestRecentSearches.push(recentSearchMatches[i].recentSearch);
    }

    // Segment
    if (bestRecentSearches.length > 0 && !observedRecents) {
        observedRecents = true;
        emitGE({
            eventaction: 'exposure.recentsearches'
        });
    }

    // Map to regular results
    bestRecentSearches = bestRecentSearches.map((recent) => {
        return {
            recentlyviewed: true,
            ...recent.selectedDestination
        };
    });

    // Store in hash for perf
    getRecentSearchesSortedHashMap[search] = bestRecentSearches;

    return bestRecentSearches;
}
